import React, { useState, useMemo } from "react";
import moment from "moment-timezone";
import { v4 as uuidV4 } from "uuid";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MultiSelect } from "@/components/ui/selectMulti";
import { useToast } from "@/components/ui/use-toast";
import { type CfrSuggestion, type ListCfrTemplateItem, useCreateBulkInstances, BulkSendInstancesValidation } from "@/lib/api/cfr";
import { CommodityOptions } from "@/lib/api/commodity";
import { getUniqueStrings } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import { useQueryClient } from "@tanstack/react-query";
import { chunkArray } from "@/lib/prelude";

interface BulkSendDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    selectedSuggestions: CfrSuggestion[];
    templateData: ListCfrTemplateItem[];
}

export function BulkSendDialog({
    open,
    onOpenChange,
    selectedSuggestions,
    templateData,
}: BulkSendDialogProps) {
    const { toast } = useToast();
    const createBulkInstances = useCreateBulkInstances();
    const queryClient = useQueryClient();

    // Get unique crop years from templates
    const cropYearOptions = useMemo(() => {
        const uniqueYears = Array.from(new Set(templateData.map(t => t.crop_year)))
            .sort((a, b) => b - a);
        return uniqueYears.map(year => ({
            value: year.toString(),
            label: year.toString(),
        }));
    }, [templateData]);

    // Get unique commodities from templates
    const commodityOptions = useMemo(() => {
        const uniqueCommodities = getUniqueStrings(templateData.map(t => t.commodity_type));
        return uniqueCommodities
            .map(commodity => {
                const option = CommodityOptions.find(opt => opt.value === commodity);
                return {
                    value: commodity,
                    label: option?.label || commodity,
                };
            })
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [templateData]);

    const currentYear = moment().year();
    const [selectedCropYear, setSelectedCropYear] = useState<string>(
        cropYearOptions.find(opt => opt.value === currentYear.toString())?.value ||
        cropYearOptions[0]?.value ||
        currentYear.toString()
    );
    const [selectedCommodities, setSelectedCommodities] = useState<string[]>([]);
    const [isTestSend, setIsTestSend] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Progress tracking state
    const [currentBatch, setCurrentBatch] = useState(0);
    const [totalBatches, setTotalBatches] = useState(0);
    const [successfulInstances, setSuccessfulInstances] = useState(0);
    const [failedInstances, setFailedInstances] = useState(0);

    const matchingTemplates = useMemo(() => {
        if (!selectedCropYear || !selectedCommodities?.length) return [];

        const selectedYear = parseInt(selectedCropYear);

        // Filter templates that match the selected crop year and commodities
        const matchingTemplates = templateData.filter(template =>
            template.crop_year === selectedYear &&
            selectedCommodities.includes(template.commodity_type)
        );

        return matchingTemplates;
    }, [templateData, selectedCropYear, selectedCommodities]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (isSubmitting) return;
        setIsSubmitting(true);

        // Reset progress tracking
        setCurrentBatch(0);
        setTotalBatches(0);
        setSuccessfulInstances(0);
        setFailedInstances(0);

        try {
            if (selectedCommodities.length === 0) {
                toast({
                    title: "Error",
                    description: "Please select at least one commodity",
                    variant: "destructive",
                });
                return;
            }

            if (selectedSuggestions.length === 0) {
                toast({
                    title: "Error",
                    description: "No suggestions selected",
                    variant: "destructive",
                });
                return;
            }

            if (matchingTemplates.length === 0) {
                toast({
                    title: "Error",
                    description: "No templates found matching the selected criteria",
                    variant: "destructive",
                });
                return;
            }

            // Filter out suggestions without customer_id
            const validSuggestions = selectedSuggestions.filter(suggestion => {
                if (!suggestion.customer_id) {
                    console.warn("Skipping suggestion without customer_id", suggestion);
                    return false;
                }
                return true;
            });

            if (validSuggestions.length === 0) {
                toast({
                    title: "Error",
                    description: "No valid suggestions to send (missing customer information)",
                    variant: "destructive",
                });
                return;
            }

            // Split suggestions into batches of 10
            const BATCH_SIZE = 10;
            const batches = chunkArray(BATCH_SIZE, validSuggestions);
            setTotalBatches(batches.length);

            let totalSuccessful = 0;
            let totalFailed = 0;

            // Process each batch sequentially
            for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
                const batch = batches[batchIndex];
                setCurrentBatch(batchIndex + 1);

                try {
                    // Build the to_send array for this batch
                    const to_send = [];
                    for (const suggestion of batch) {
                        for (const cfr_template of matchingTemplates) {
                            to_send.push({
                                cfr_document_id: cfr_template.id,
                                group_id: uuidV4(),
                                template_id: cfr_template.DocumentTemplate!.id,
                                customer_id: suggestion.customer_id!,

                                is_test_send: isTestSend,
                                is_draft: false,

                                suggestion_id: suggestion.id,
                            });
                        }
                    }

                    // Validate the payload
                    const parseResult = BulkSendInstancesValidation.safeParse({ to_send });

                    if (!parseResult.success) {
                        console.error("Failed to validate bulk send data for batch", batchIndex + 1, parseResult.error);
                        totalFailed += to_send.length;
                        setFailedInstances(totalFailed);
                        continue; // Skip this batch but continue with others
                    }

                    // Call the API for this batch
                    const result = await createBulkInstances(parseResult.data);

                    if (result.success) {
                        totalSuccessful += to_send.length;
                        setSuccessfulInstances(totalSuccessful);
                    } else {
                        console.error("Failed to send batch", batchIndex + 1, result);
                        totalFailed += to_send.length;
                        setFailedInstances(totalFailed);
                    }
                } catch (error) {
                    console.error("Error processing batch", batchIndex + 1, error);
                    // Count failed instances for this batch
                    const batchInstanceCount = batch.length * matchingTemplates.length;
                    totalFailed += batchInstanceCount;
                    setFailedInstances(totalFailed);
                    // Continue with next batch
                }
            }

            queryClient.invalidateQueries({ queryKey: ["cfr"] });

            // Show final summary toast
            const totalInstances = totalSuccessful + totalFailed;
            if (totalFailed === 0) {
                toast({
                    title: "Bulk Send Completed",
                    description: `Successfully queued ${totalSuccessful} of ${validSuggestions.length}.`,
                });
            } else if (totalSuccessful === 0) {
                toast({
                    title: "Bulk Send Failed",
                    description: `Failed to queue all ${totalInstances} contracts`,
                    variant: "destructive",
                });
            } else {
                toast({
                    title: "Bulk Send Partially Completed",
                    description: `Queued ${totalSuccessful} of ${validSuggestions.length} contracts. ${totalFailed} failed.`,
                    variant: "destructive",
                });
            }

            // Reset form and close dialog on success
            if (totalSuccessful > 0) {
                setSelectedCommodities([]);
                onOpenChange(false);
            }

        } catch (error) {
            console.error("Failed to queue bulk instances", error);
            const errorMessage = error instanceof Error ? error.message : "Failed to queue bulk instances";
            toast({
                title: "Error",
                description: errorMessage,
                variant: "destructive",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle>Bulk Send CFR Templates</DialogTitle>
                </DialogHeader>

                <form onSubmit={handleSubmit}>
                    {isSubmitting ? (
                        // Progress indicator UI
                        <div className="grid gap-4 py-8">
                            <div className="text-center space-y-4">
                                <div className="text-lg font-semibold">Sending Contracts...</div>
                                
                                <div className="space-y-2">
                                    <div className="text-sm text-muted-foreground">
                                        Processing batch {currentBatch} of {totalBatches}
                                    </div>
                                    
                                    {/* Progress bar */}
                                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                                        <div 
                                            className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                                            style={{ 
                                                width: `${totalBatches > 0 ? (currentBatch / totalBatches) * 100 : 0}%` 
                                            }}
                                        ></div>
                                    </div>
                                    
                                    <div className="text-sm text-muted-foreground">
                                        {totalBatches > 0 ? Math.round((currentBatch / totalBatches) * 100) : 0}% complete
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4 pt-4">
                                    <div className="text-center p-3 bg-green-50 rounded-lg">
                                        <div className="text-sm text-muted-foreground">Successful</div>
                                        <div className="text-2xl font-bold text-green-600">{successfulInstances}</div>
                                    </div>
                                    <div className="text-center p-3 bg-red-50 rounded-lg">
                                        <div className="text-sm text-muted-foreground">Failed</div>
                                        <div className="text-2xl font-bold text-red-600">{failedInstances}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        // Form UI
                        <>
                            <div className="grid gap-4 py-4">
                                <div className="grid gap-2 md:grid-cols-2">
                                    <div className="grid gap-2">
                                        <Label>Selected Customers</Label>
                                        <div className="text-sm text-muted-foreground">
                                            {selectedSuggestions.length} customer(s) selected
                                        </div>
                                    </div>
                                    <div className="grid gap-2">
                                        <Label>Implied Templates</Label>
                                        <div className="text-sm text-muted-foreground">
                                            {matchingTemplates.length} templates(s) selected
                                        </div>
                                    </div>
                                </div>

                                <div className="grid gap-2">
                                    <Label htmlFor="is_test_send">Is Test Send</Label>
                                    <Checkbox
                                        id="is_test_send"
                                        checked={isTestSend}
                                        onCheckedChange={(checked) => setIsTestSend(!!checked)}
                                    />
                                </div>



                                <div className="grid gap-2">
                                    <Label htmlFor="crop_year">Crop Year</Label>
                                    <Select
                                        value={selectedCropYear}
                                        onValueChange={setSelectedCropYear}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select crop year" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {cropYearOptions.map((option) => (
                                                <SelectItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="grid gap-2">
                                    <Label htmlFor="commodities">Commodities</Label>
                                    <MultiSelect
                                        values={selectedCommodities}
                                        setValues={setSelectedCommodities}
                                        options={commodityOptions}
                                        listClassName="max-h-[200px]"
                                    />
                                    <div className="text-xs text-muted-foreground">
                                        Select one or more commodities
                                    </div>
                                </div>
                            </div>

                            <DialogFooter>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => onOpenChange(false)}
                                    disabled={isSubmitting}
                                >
                                    Cancel
                                </Button>
                                <Button type="submit" disabled={isSubmitting}>
                                    {isSubmitting ? "Sending..." : "Send"}
                                </Button>
                            </DialogFooter>
                        </>
                    )}
                </form>
            </DialogContent>
        </Dialog>
    );
}

